import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, ExclamationCircleOutlined, CloseOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import styles from './styles.module.less';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationProps {
  type?: NotificationType;
  message: React.ReactNode;
  description?: React.ReactNode;
  duration?: number;
  onClose?: () => void;
  className?: string;
  showIcon?: boolean;
  placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
}

const iconMap = {
  success: CheckCircleOutlined,
  error: CloseCircleOutlined,
  warning: ExclamationCircleOutlined,
  info: InfoCircleOutlined,
};

export const Notification: React.FC<NotificationProps> = ({
  type = 'info',
  message,
  description,
  duration = 4.5,
  onClose,
  className,
  showIcon = true,
  placement = 'topRight',
}) => {
  const [visible, setVisible] = useState(true);
  const Icon = iconMap[type];

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300); // Wait for animation to complete
  }, [onClose]);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration * 1000);

      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className={classNames(
        styles.notification,
        styles[type],
        styles[placement],
        className
      )}
    >
      {showIcon && (
        <span className={styles.icon}>
          <Icon />
        </span>
      )}
      <div className={styles.content}>
        <div className={styles.message}>{message}</div>
        {description && (
          <div className={styles.description}>{description}</div>
        )}
      </div>
      <button
        type="button"
        className={styles.closeBtn}
        onClick={handleClose}
        aria-label="Close"
      >
        <CloseOutlined />
      </button>
    </div>
  );
};

// Notification Container Component
export interface NotificationContainerProps {
  notifications: Array<NotificationProps & { id: string }>;
  onRemove: (id: string) => void;
  placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
}

export const NotificationContainer: React.FC<NotificationContainerProps> = ({
  notifications,
  onRemove,
  placement = 'topRight',
}) => {
  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className={classNames(styles.container, styles[placement])}>
      {notifications.map((notification) => (
        <Notification
          key={notification.id}
          {...notification}
          placement={placement}
          onClose={() => onRemove(notification.id)}
        />
      ))}
    </div>
  );
};

